/**
 * Fontes exportáveis: Central de Importação de Listas
 * -----------------------------------------------------------------------------
 * Uma única fábrica gera as quatro fontes (Contatos, Negócios, Clientes e
 * Afiliados). Todas leem as linhas pela mesma função do banco
 * (`get_list_export_rows`), que valida tenant, permissão de exportação, teto de
 * registros e auditoria num só lugar.
 *
 * As colunas do arquivo são DINÂMICAS: dependem dos campos daquele arquivo
 * importado. Por isso a fonte implementa `resolveColumns`, lendo a primeira
 * linha da lista para descobrir os campos disponíveis.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-12 v1.0.0 - Criação inicial (exportação das listas importadas).
 */

import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { EXPORT_ROW_LIMIT, type ExportColumn, type ExportParamSummary, type ExportSource } from '../types';

const logger = createLogger('ExportLists');

/** Tamanho do bloco de leitura (abaixo do teto de 1.000 da camada de API) */
const LOTE = 500;

/** Chaves fixas acrescentadas a toda exportação de lista */
const COLUNA_LINHA = '__linha';
const COLUNA_SITUACAO = '__situacao';
const COLUNA_ERRO = '__erro';

/** Parâmetros da tela: qual lista/importação está sendo exportada */
export interface ListsExportParams {
  /** Id da lista (Contatos) ou do job de importação (demais abas) */
  refId: string;
  /** Nome exibido na tela, usado no resumo da auditoria */
  name?: string;
}

/** Linha devolvida pela RPC genérica */
interface LinhaExportacao {
  row_index: number | null;
  status: string | null;
  error_message: string | null;
  raw_row: Record<string, unknown> | null;
  total_count: number | null;
}

/** Chama a função genérica do banco para um bloco de linhas */
async function buscarBloco(
  sourceId: string,
  tenantId: string,
  refId: string,
  limite: number,
  deslocamento: number,
): Promise<LinhaExportacao[]> {
  const { data, error } = await supabase.rpc('get_list_export_rows', {
    p_source: sourceId,
    p_ref_id: refId,
    p_limit: limite,
    p_offset: deslocamento,
    p_tenant_hint: tenantId,
  } as never);

  if (error) throw error;
  return (data as unknown as LinhaExportacao[]) || [];
}

/** Converte a chave técnica do arquivo em um rótulo legível */
function rotulo(chave: string): string {
  const limpo = chave.replace(/[_-]+/g, ' ').trim();
  return limpo.charAt(0).toUpperCase() + limpo.slice(1);
}

/** Monta a linha "plana" do arquivo a partir da linha devolvida pelo banco */
function achatar(linha: LinhaExportacao): Record<string, unknown> {
  const bruto = (linha.raw_row || {}) as Record<string, unknown>;
  const plana: Record<string, unknown> = {
    [COLUNA_LINHA]: linha.row_index ?? null,
    [COLUNA_SITUACAO]: linha.status ?? null,
    [COLUNA_ERRO]: linha.error_message ?? null,
  };
  for (const [chave, valor] of Object.entries(bruto)) {
    // Valores compostos (objetos/arrays) viram texto para caber na planilha
    plana[chave] =
      valor !== null && typeof valor === 'object' ? JSON.stringify(valor) : (valor as unknown);
  }
  return plana;
}

/** Cria a fonte exportável de uma aba da Central de Listas */
function criarFonte(config: {
  id: string;
  label: string;
  title: string;
  moduleKey: string;
  fileBaseName: string;
}): ExportSource<ListsExportParams> {
  return {
    id: config.id,
    label: config.label,
    title: config.title,
    moduleKey: config.moduleKey,
    fileBaseName: config.fileBaseName,

    // Colunas fixas (usadas apenas como base; as reais vêm de resolveColumns)
    columns: [
      { key: COLUNA_LINHA, label: 'Linha', group: 'Importação', kind: 'number', defaultSelected: true },
      { key: COLUNA_SITUACAO, label: 'Situação da linha', group: 'Importação', defaultSelected: true },
      { key: COLUNA_ERRO, label: 'Erro', group: 'Importação', defaultSelected: true },
    ],

    /** Descobre os campos daquele arquivo lendo a primeira linha da lista */
    async resolveColumns(tenantId, params) {
      const base: ExportColumn[] = [
        { key: COLUNA_LINHA, label: 'Linha', group: 'Importação', kind: 'number', defaultSelected: true },
        { key: COLUNA_SITUACAO, label: 'Situação da linha', group: 'Importação', defaultSelected: true },
        { key: COLUNA_ERRO, label: 'Erro', group: 'Importação', defaultSelected: true },
      ];

      try {
        const primeiras = await buscarBloco(config.id, tenantId, params.refId, 1, 0);
        const bruto = (primeiras[0]?.raw_row || {}) as Record<string, unknown>;
        const doArquivo: ExportColumn[] = Object.keys(bruto).map((chave) => ({
          key: chave,
          label: rotulo(chave),
          group: 'Campos do arquivo',
          defaultSelected: true,
        }));
        return [...doArquivo, ...base];
      } catch (erro) {
        logger.error('Falha ao descobrir os campos da lista:', erro);
        return base;
      }
    },

    /** Conta quantas linhas a lista possui */
    async countRows(tenantId, params) {
      const primeiras = await buscarBloco(config.id, tenantId, params.refId, 1, 0);
      return Number(primeiras[0]?.total_count ?? 0);
    },

    /** Lê a lista inteira em blocos, respeitando o teto de segurança */
    async fetchRows(tenantId, params) {
      const linhas: Record<string, unknown>[] = [];
      let deslocamento = 0;

      while (linhas.length < EXPORT_ROW_LIMIT) {
        const restante = Math.min(LOTE, EXPORT_ROW_LIMIT - linhas.length);
        const bloco = await buscarBloco(config.id, tenantId, params.refId, restante, deslocamento);
        if (bloco.length === 0) break;
        linhas.push(...bloco.map(achatar));
        if (bloco.length < restante) break;
        deslocamento += bloco.length;
      }

      return linhas;
    },

    /** Resumo legível gravado na auditoria */
    async describeParams(_tenantId, params): Promise<ExportParamSummary[]> {
      return [
        { group: 'filtro', label: 'Lista', value: params.name || params.refId.slice(0, 8).toUpperCase() },
        { group: 'filtro', label: 'Conteúdo', value: 'Lista completa (todas as linhas)' },
      ];
    },
  };
}

/** Contatos — listas de campanha importadas */
export const listsContactsExportSource = criarFonte({
  id: 'lists_contacts',
  label: 'Listas de contatos',
  title: 'Lista de contatos importada',
  moduleKey: 'lists',
  fileBaseName: 'lista_contatos',
});

/** Negócios — importações de negócios */
export const listsDealsExportSource = criarFonte({
  id: 'lists_deals',
  label: 'Listas de negócios',
  title: 'Importação de negócios',
  moduleKey: 'lists',
  fileBaseName: 'lista_negocios',
});

/** Clientes — importações de clientes */
export const listsCustomersExportSource = criarFonte({
  id: 'lists_customers',
  label: 'Listas de clientes',
  title: 'Importação de clientes',
  moduleKey: 'lists',
  fileBaseName: 'lista_clientes',
});

/** Afiliados — importações de afiliados */
export const listsAffiliatesExportSource = criarFonte({
  id: 'lists_affiliates',
  label: 'Listas de afiliados',
  title: 'Importação de afiliados',
  moduleKey: 'lists',
  fileBaseName: 'lista_afiliados',
});
